import {useState, useEffect, useRef, useCallback} from 'react';
import {Form, Modal, message} from 'antd';
import {createSalt, isEncrypted, unlockVault} from '../crypt';
import {clearYandexSyncStorage, YANDEX_OAUTH_RETURN_KEY} from '../SyncSettings';
import {
    clearIdentity,
    ensureIdentityForSalt,
    getIdentityExportMeta,
} from '../account';
import Package from '../../package.json';

export const appVersion = Package.version;
export const IDLE_LOCK_MS = 5 * 60 * 1000;
export const CLIPBOARD_TIMEOUT_MS = 15000;
export const GENERATED_PASSWORD_LENGTH = 16;
export const PASSWORD_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
export const WELCOME_SEEN_KEY = 'selfpass_welcome_seen';

const hasStoredSalt = () => Boolean(localStorage.getItem('salt'));
const hasSeenWelcome = () => localStorage.getItem(WELCOME_SEEN_KEY) === '1';

const usePasswordManager = () => {
    const [form] = Form.useForm();
    const [authForm] = Form.useForm();
    const [passwords, setPasswords] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPassword, setEditingPassword] = useState(null);
    const [authVisible, setAuthVisible] = useState(true);
    const [vaultSession, setVaultSession] = useState(null);
    const [copiedPasswordId, setCopiedPasswordId] = useState(null);
    const [copiedLoginId, setCopiedLoginId] = useState(null);
    const [welcomeVisible, setWelcomeVisible] = useState(() => !hasStoredSalt() && !hasSeenWelcome());
    const [saltChoiceVisible, setSaltChoiceVisible] = useState(() => !hasStoredSalt() && hasSeenWelcome());
    const [newSalt, setNewSalt] = useState('');
    const [customSalt, setCustomSalt] = useState('');
    const [step, setStep] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isNewVault, setIsNewVault] = useState(() => !localStorage.getItem('encryptedPasswords'));
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [syncSettingsKey, setSyncSettingsKey] = useState(0);
    const [isYandexSyncing, setIsYandexSyncing] = useState(false);
    const [accountInfo, setAccountInfo] = useState(() => getIdentityExportMeta());
    const [activePage, setActivePage] = useState(() => {
        const hash = window.location.hash.substr(1);
        const params = new URLSearchParams(hash);
        if (params.get('access_token') || sessionStorage.getItem(YANDEX_OAUTH_RETURN_KEY) === 'settings') {
            return 'settings';
        }
        return 'vault';
    });

    const clipboardTimeoutRef = useRef(0);
    const idleTimerRef = useRef(0);

    const lockVault = useCallback(() => {
        setPasswords([]);
        setVaultSession(null);
        setAuthVisible(true);
        setIsModalVisible(false);
        setEditingPassword(null);
        setSearchTerm('');
        setActivePage('vault');
        form.resetFields();
        authForm.resetFields();
        setIsNewVault(!localStorage.getItem('encryptedPasswords'));
        message.info('Сессия заблокирована');
    }, [form, authForm]);

    const refreshAccountInfo = useCallback(() => {
        setAccountInfo(getIdentityExportMeta());
    }, []);

    const exitVault = useCallback(() => {
        localStorage.removeItem('encryptedPasswords');
        localStorage.removeItem('salt');
        clearIdentity();
        clearYandexSyncStorage();
        setPasswords([]);
        setVaultSession(null);
        setAuthVisible(true);
        setIsModalVisible(false);
        setEditingPassword(null);
        setSearchTerm('');
        setActivePage('vault');
        form.resetFields();
        authForm.resetFields();
        setIsNewVault(true);
        setCustomSalt('');
        setNewSalt('');
        setStep(1);
        setWelcomeVisible(false);
        setSaltChoiceVisible(true);
        setSyncSettingsKey((key) => key + 1);
        setAccountInfo(null);
        message.info('Хранилище сброшено. Можно создать новое или открыть другое.');
    }, [form, authForm]);

    const confirmExitVault = useCallback(() => {
        Modal.confirm({
            title: 'Выйти из хранилища?',
            content: 'Локальные данные сейфа будут удалены с этого устройства. Если они ещё нужны — сначала экспортируйте их. После выхода можно создать новое хранилище или открыть другое (с другим ключом).',
            okText: 'Выйти',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: exitVault,
        });
    }, [exitVault]);

    const resetIdleTimer = useCallback(() => {
        if (authVisible || !vaultSession) return;
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        idleTimerRef.current = setTimeout(lockVault, IDLE_LOCK_MS);
    }, [authVisible, vaultSession, lockVault]);

    useEffect(() => {
        if (authVisible || !vaultSession) return;

        const saveData = async () => {
            const encrypted = await vaultSession.encrypt(passwords);
            if (encrypted) {
                localStorage.setItem('encryptedPasswords', encrypted);
                setIsNewVault(false);
            }
        };

        saveData();
    }, [passwords, vaultSession, authVisible]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText('');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (clipboardTimeoutRef.current) {
                clearTimeout(clipboardTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const storedSalt = localStorage.getItem('salt');
        if (!storedSalt) {
            if (hasSeenWelcome()) {
                setWelcomeVisible(false);
                setSaltChoiceVisible(true);
            } else {
                setWelcomeVisible(true);
                setSaltChoiceVisible(false);
            }
            return;
        }
        setWelcomeVisible(false);
        ensureIdentityForSalt(storedSalt)
            .then(() => refreshAccountInfo())
            .catch((error) => {
                console.error('Failed to migrate account identity:', error);
            });
    }, [refreshAccountInfo]);

    const handleWelcomeContinue = useCallback(() => {
        localStorage.setItem(WELCOME_SEEN_KEY, '1');
        setWelcomeVisible(false);
        setStep(1);
        setSaltChoiceVisible(true);
    }, []);

    useEffect(() => {
        if (sessionStorage.getItem(YANDEX_OAUTH_RETURN_KEY) !== 'settings') {
            return undefined;
        }

        setActivePage('settings');

        if (!authVisible && vaultSession) {
            sessionStorage.removeItem(YANDEX_OAUTH_RETURN_KEY);
        }

        return undefined;
    }, [authVisible, vaultSession]);

    useEffect(() => {
        if (authVisible || !vaultSession) {
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
            }
            return undefined;
        }

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        const onActivity = () => resetIdleTimer();

        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, onActivity, {passive: true});
        });
        resetIdleTimer();

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, onActivity);
            });
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
            }
        };
    }, [authVisible, vaultSession, resetIdleTimer]);

    const handleSaltChoice = (choice) => {
        if (choice === 'generate') {
            const generatedSalt = createSalt();
            setNewSalt(generatedSalt);
            setStep(2);
        } else {
            setStep(3);
        }
    };

    const handleCustomSaltSubmit = async () => {
        if (!customSalt || customSalt.length < 16) {
            message.error('Ключ должен быть не менее 16 символов');
            return;
        }
        localStorage.setItem('salt', customSalt);
        await ensureIdentityForSalt(customSalt);
        refreshAccountInfo();
        setSaltChoiceVisible(false);
        message.success('Ключ установлен. Создан неподтверждённый аккаунт');
    };

    const handleNewSaltConfirm = async () => {
        localStorage.setItem('salt', newSalt);
        await ensureIdentityForSalt(newSalt);
        refreshAccountInfo();
        setSaltChoiceVisible(false);
        message.success('Ключ создан. Создан неподтверждённый аккаунт');
    };

    const generatePassword = () => {
        const values = crypto.getRandomValues(new Uint32Array(GENERATED_PASSWORD_LENGTH));
        let password = '';
        for (let i = 0; i < GENERATED_PASSWORD_LENGTH; i += 1) {
            password += PASSWORD_CHARS.charAt(values[i] % PASSWORD_CHARS.length);
        }
        form.setFieldsValue({password});
    };

    const scheduleClipboardClear = () => {
        if (clipboardTimeoutRef.current) {
            clearTimeout(clipboardTimeoutRef.current);
        }
        clipboardTimeoutRef.current = setTimeout(async () => {
            try {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText('');
                }
            } catch (error) {
                console.error('Ошибка очистки буфера:', error);
            }
        }, CLIPBOARD_TIMEOUT_MS);
    };

    const copyToClipboard = (text, key, type = 'password') => {
        const onCopied = () => {
            message.success(type === 'password' ? 'Пароль скопирован' : 'Логин скопирован');
            scheduleClipboardClear();
            if (type === 'password') {
                setCopiedPasswordId(key);
                setTimeout(() => setCopiedPasswordId(null), 2000);
            } else {
                setCopiedLoginId(key);
                setTimeout(() => setCopiedLoginId(null), 2000);
            }
        };

        if (!navigator.clipboard?.writeText) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.setAttribute('readonly', '');
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                onCopied();
            } catch (err) {
                message.error('Не удалось скопировать');
            }
            document.body.removeChild(textArea);
            return;
        }

        navigator.clipboard.writeText(text)
            .then(onCopied)
            .catch(() => message.error('Не удалось скопировать'));
    };

    const handleAuth = async (values) => {
        const {password} = values;
        const storedSalt = localStorage.getItem('salt');

        if (!storedSalt) {
            message.error('Ключ не найден!');
            return;
        }

        setIsUnlocking(true);
        try {
            await ensureIdentityForSalt(storedSalt);
            refreshAccountInfo();
            const encryptedData = localStorage.getItem('encryptedPasswords');
            const result = await unlockVault(password, storedSalt, encryptedData);

            if (!result) {
                message.error('Неверный мастер-пароль');
                return;
            }

            const {session, data, migrated} = result;

            if (encryptedData && data) {
                const migratedEntries = data.map(item => ({
                    ...item,
                    deletedAt: item.deletedAt || null,
                    updatedAt: item.updatedAt || new Date().toISOString()
                }));
                setPasswords(migratedEntries);

                if (migrated) {
                    const reencrypted = await session.encrypt(migratedEntries);
                    if (reencrypted) {
                        localStorage.setItem('encryptedPasswords', reencrypted);
                        message.success('Сейф обновлён до AES-GCM');
                    }
                }
            } else if (!encryptedData) {
                const emptyVault = await session.encrypt([]);
                if (emptyVault) {
                    localStorage.setItem('encryptedPasswords', emptyVault);
                }
                setPasswords([]);
                setIsNewVault(false);
            }

            setVaultSession(session);
            setAuthVisible(false);
            authForm.resetFields();
            message.success('Добро пожаловать!');
        } catch (error) {
            console.error('Authentication error:', error);
            message.error('Ошибка аутентификации');
        } finally {
            setIsUnlocking(false);
        }
    };

    const handleExportEncrypted = async () => {
        try {
            if (!vaultSession) {
                message.error('Ключ шифрования не инициализирован');
                return;
            }

            if (passwords.length === 0) {
                message.warning('Нет данных для экспорта');
                return;
            }

            const identityMeta = getIdentityExportMeta();
            const activePasswords = passwords.filter(pwd => !pwd.deletedAt);

            const exportData = {
                passwords: activePasswords.map(pwd => ({
                    id: pwd.key.toString(),
                    fields: {
                        name: pwd.name || '',
                        login: pwd.username || '',
                        password: pwd.password || '',
                        url: pwd.url || ''
                    },
                    vaultName: identityMeta?.vaultName || 'Основной сейф',
                    vaultId: identityMeta?.vaultId || 'default-vault-id',
                    accountId: identityMeta?.accountId || null,
                    updatedAt: pwd.updatedAt || new Date().toISOString()
                })),
                meta: {
                    encrypted: true,
                    timestamp: new Date().toISOString(),
                    version: "2.0",
                    totalRecords: activePasswords.length,
                    accountId: identityMeta?.accountId || null,
                    accountStatus: identityMeta?.accountStatus || null,
                    vaultId: identityMeta?.vaultId || null,
                    vaultName: identityMeta?.vaultName || null,
                }
            };

            const encryptedExport = await vaultSession.encrypt(exportData);

            if (!encryptedExport) {
                throw new Error('Не удалось зашифровать данные');
            }

            const blob = new Blob([encryptedExport], {
                type: 'application/json;charset=utf-8'
            });

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `selfpass_export_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            message.success(`Экспортировано ${activePasswords.length} записей (зашифровано)`);
        } catch (error) {
            console.error('Export error:', error);
            message.error('Ошибка экспорта: ' + error.message);
        }
    };

    const handleExportForExtension = () => {
        const salt = localStorage.getItem('salt');
        const encryptedPasswords = localStorage.getItem('encryptedPasswords');
        const identityMeta = getIdentityExportMeta();

        if (!salt || !encryptedPasswords) {
            message.error('Нет локального сейфа для экспорта');
            return;
        }

        const payload = {
            salt,
            encryptedPasswords,
            identity: identityMeta,
            meta: {
                purpose: 'selfpass-extension',
                timestamp: new Date().toISOString(),
                version: appVersion,
                accountId: identityMeta?.accountId || null,
                accountStatus: identityMeta?.accountStatus || null,
                vaultId: identityMeta?.vaultId || null,
            },
        };

        const blob = new Blob([JSON.stringify(payload, null, 2)], {
            type: 'application/json;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `selfpass_extension_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        message.success('Файл для расширения сохранён');
    };

    const handleImport = (file) => {
        Modal.confirm({
            title: 'Вы уверены?',
            content: 'Текущие данные будут заменены импортированными',
            onOk: async () => {
                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        let data;
                        const fileContent = e.target.result;

                        if (!fileContent || fileContent.trim() === '') {
                            throw new Error('Файл пуст или поврежден');
                        }

                        if (isEncrypted(fileContent)) {
                            if (!vaultSession) {
                                throw new Error('Сейф заблокирован');
                            }
                            data = await vaultSession.decrypt(fileContent);

                            if (!data) {
                                throw new Error('Не удалось расшифровать данные. Проверьте правильность мастер-пароля.');
                            }
                        } else {
                            try {
                                data = JSON.parse(fileContent);
                            } catch (parseError) {
                                throw new Error('Неверный формат JSON файла: ' + parseError.message);
                            }
                        }

                        if (!data || typeof data !== 'object') {
                            throw new Error('Неверная структура данных');
                        }

                        if (!data.passwords || !Array.isArray(data.passwords)) {
                            throw new Error('В файле отсутствует массив паролей');
                        }

                        const identityMeta = getIdentityExportMeta();
                        const importedAccountId =
                            data.meta?.accountId
                            || data.passwords.find((pwd) => pwd?.accountId)?.accountId
                            || null;

                        if (
                            identityMeta?.accountId
                            && importedAccountId
                            && importedAccountId !== identityMeta.accountId
                        ) {
                            throw new Error(
                                'Файл принадлежит другому аккаунту. Импорт возможен только в сейф того же аккаунта (в т.ч. неподтверждённого).'
                            );
                        }

                        const importedPasswords = data.passwords.map((pwd, index) => {
                            if (!pwd || typeof pwd !== 'object') {
                                console.warn(`Пропускаем некорректную запись ${index}`);
                                return null;
                            }

                            return {
                                key: pwd.id || `imported_${Date.now()}_${index}`,
                                name: pwd.fields?.name || pwd.name || `Импорт ${index + 1}`,
                                username: pwd.fields?.login || pwd.fields?.username || pwd.username || '',
                                password: pwd.fields?.password || pwd.password || '',
                                url: pwd.fields?.url || pwd.url || '',
                                updatedAt: new Date().toISOString(),
                                deletedAt: null
                            };
                        }).filter(Boolean);

                        if (importedPasswords.length === 0) {
                            throw new Error('Не найдено валидных записей для импорта');
                        }

                        setPasswords(importedPasswords);
                        message.success(`Успешно импортировано ${importedPasswords.length} записей`);
                    } catch (err) {
                        console.error('Import error:', err);
                        message.error('Ошибка импорта: ' + err.message);
                    }
                };

                reader.onerror = () => {
                    message.error('Ошибка чтения файла');
                };

                reader.readAsText(file, 'UTF-8');
            }
        });
    };

    const handleAdd = () => {
        setEditingPassword(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingPassword(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = (key) => {
        Modal.confirm({
            title: 'Удалить запись?',
            content: 'Это действие нельзя отменить',
            onOk: () => {
                setPasswords(prev => prev.map(item => {
                    const deletedAt = new Date();
                    const updatedAt = new Date(deletedAt.getTime() + 1000);
                    return item.key === key
                        ? {
                            ...item,
                            deletedAt: deletedAt.toISOString(),
                            updatedAt: updatedAt.toISOString(),
                        }
                        : item;
                }));
                message.success('Запись помечена как удаленная');
            },
        });
    };

    const handleRestore = (key) => {
        setPasswords(prev => prev.map(item =>
            item.key === key
                ? {...item, deletedAt: null, updatedAt: new Date().toISOString()}
                : item
        ));
        message.success('Запись восстановлена');
    };

    const handleSubmit = () => {
        form.validateFields().then(values => {
            const newPassword = {
                ...values,
                key: editingPassword ? editingPassword.key : Date.now(),
                updatedAt: new Date().toISOString()
            };

            setPasswords(prev =>
                editingPassword
                    ? prev.map(item => item.key === newPassword.key ? newPassword : item)
                    : [...prev, newPassword]
            );

            message.success(`Пароль ${editingPassword ? 'обновлён' : 'добавлен'}`);
            setIsModalVisible(false);
        });
    };

    const handleMenuClick = (e) => {
        if (e.key === 'settings') {
            setActivePage('settings');
        }

        if (e.key === 'lock') {
            lockVault();
        }

        if (e.key === 'exit') {
            confirmExitVault();
        }
    };

    const filteredPasswords = passwords.filter(password =>
        password.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return {
        form,
        authForm,
        passwords,
        isModalVisible,
        setIsModalVisible,
        editingPassword,
        authVisible,
        vaultSession,
        copiedPasswordId,
        copiedLoginId,
        welcomeVisible,
        saltChoiceVisible,
        newSalt,
        setNewSalt,
        customSalt,
        setCustomSalt,
        step,
        setStep,
        searchTerm,
        setSearchTerm,
        isNewVault,
        isUnlocking,
        syncSettingsKey,
        isYandexSyncing,
        setIsYandexSyncing,
        accountInfo,
        activePage,
        setActivePage,
        lockVault,
        confirmExitVault,
        handleWelcomeContinue,
        handleSaltChoice,
        handleCustomSaltSubmit,
        handleNewSaltConfirm,
        generatePassword,
        copyToClipboard,
        handleAuth,
        handleExportEncrypted,
        handleExportForExtension,
        handleImport,
        handleAdd,
        handleEdit,
        handleDelete,
        handleRestore,
        handleSubmit,
        handleMenuClick,
        filteredPasswords,
        setPasswords,
    };
};

export default usePasswordManager;
